import React, { useEffect } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  buildNotificationUrl, formatEventType, eventTypeColor,
  siteOf, participantOf, studyOf,
} from './notificationNavigation';

// Dark-mode tints for the type chips. The light classes, and the colour for each
// event family, come from eventTypeColor in notificationNavigation.js.
const TYPE_CHIP_DARK = {
  blue: 'dark:bg-blue-500/15 dark:text-blue-300',
  green: 'dark:bg-green-500/15 dark:text-green-300',
  purple: 'dark:bg-purple-500/15 dark:text-purple-300',
  amber: 'dark:bg-amber-500/15 dark:text-amber-300',
  teal: 'dark:bg-teal-500/15 dark:text-teal-300',
  indigo: 'dark:bg-indigo-500/15 dark:text-indigo-300',
  gray: 'dark:bg-white/[0.08] dark:text-gray-300',
};

export const typeChipClass = (eventTypeKey) => {
  const { badge } = eventTypeColor(eventTypeKey);
  const family = (badge.match(/\bbg-([a-z]+)-100\b/) || [])[1];
  return `${badge} ${TYPE_CHIP_DARK[family] || TYPE_CHIP_DARK.gray}`;
};

/**
 * Full detail view for a single notification. Opened from both the
 * notifications table and the bell dropdown so the (often truncated)
 * title/body can be read in full, with an optional deep link into the app.
 */
const NotificationDetailModal = ({ notification, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!notification) return null;

  const url = buildNotificationUrl(notification);
  const received = new Date(notification.created_at).toLocaleString(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  });

  const meta = [
    { label: 'Site', value: siteOf(notification) },
    { label: 'Participant', value: participantOf(notification) },
    { label: 'Study', value: studyOf(notification) },
    { label: 'Received', value: received },
    { label: 'From', value: notification.actor_name || 'System' },
  ].filter((m) => m.value);

  const handleGo = () => {
    onClose();
    navigate(url);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Notification details"
        className="w-full max-w-lg animate-slide-up overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${typeChipClass(notification.event_type_key)}`}>
              {formatEventType(notification.event_type_key)}
            </span>
            <h3 className="mt-2 break-words text-lg font-semibold text-gray-900 dark:text-gray-50">
              {notification.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="btn btn-ghost btn-sm btn-icon -mr-2 flex-shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-5 max-h-[50vh] overflow-y-auto">
          {notification.body && (
            <p className="whitespace-pre-line break-words text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              {notification.body}
            </p>
          )}
          {meta.length > 0 && (
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-gray-100 pt-4 text-sm dark:border-gray-700">
              {meta.map((m) => (
                <div key={m.label}>
                  <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{m.label}</dt>
                  <dd className="mt-0.5 text-gray-700 dark:text-gray-200">{m.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Close
          </button>
          {url && (
            <button
              onClick={handleGo}
              className="btn btn-primary"
            >
              Go to related page <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationDetailModal;
